/**
 * AppointmentsConfig — FASE 1 + Fase 4.2 (0257)
 * Contenido por submenu: al elegir una opción se muestra solo esa sección con acción EDITAR real.
 * Equipo: tabla inline con "Vincular con usuario" + enlace a onboarding para nombres/roles.
 * Secuencias: Agente IA para flujo de citas + enlace a Prompts IA (tono y personalidad).
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import BusinessIcon from "@material-ui/icons/Business";
import PersonIcon from "@material-ui/icons/Person";
import ScheduleIcon from "@material-ui/icons/Schedule";
import CategoryIcon from "@material-ui/icons/Category";
import SettingsIcon from "@material-ui/icons/Settings";
import EditIcon from "@material-ui/icons/Edit";
import { toast } from "react-toastify";
import { useStyles } from "./styles";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const CONFIG_CARD_DEFS = [
  { id: "perfil", titleKey: "cardPerfilTitle", descKey: "cardPerfilDesc", icon: BusinessIcon, onboardingStep: 0 },
  { id: "servicios", titleKey: "cardServiciosTitle", descKey: "cardServiciosDesc", icon: CategoryIcon, onboardingStep: 4 },
  { id: "equipo", titleKey: "cardEquipoTitle", descKey: "cardEquipoDesc", icon: PersonIcon, onboardingStep: 3 },
  { id: "horarios", titleKey: "cardHorariosTitle", descKey: "cardHorariosDesc", icon: ScheduleIcon, onboardingStep: 2 },
  { id: "secuencias", titleKey: "cardSecuenciasTitle", descKey: "cardSecuenciasDesc", icon: SettingsIcon, onboardingStep: null }
];

export default function AppointmentsConfig() {
  const classes = useStyles();
  const history = useHistory();
  const [submenuIndex, setSubmenuIndex] = useState(0);
  const [profile, setProfile] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recrearKanbanLoading, setRecrearKanbanLoading] = useState(false);
  const [professionals, setProfessionals] = useState([]);
  const [users, setUsers] = useState([]);
  const [equipoLoading, setEquipoLoading] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [appointmentPromptId, setAppointmentPromptId] = useState(null);
  const [secuenciasLoading, setSecuenciasLoading] = useState(false);

  const configCards = useMemo(
    () =>
      CONFIG_CARD_DEFS.map((c) => ({
        ...c,
        title: i18n.t(`appointmentsModule.config.${c.titleKey}`),
        desc: i18n.t(`appointmentsModule.config.${c.descKey}`)
      })),
    [i18n.language]
  );

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/onboarding/config");
        if (data?.business) {
          if (data.business.profileName) setProfile(data.business.profileName);
          if (data.business.profileId != null) setProfileId(data.business.profileId);
        }
      } catch (e) {
        // No bloquear si falla
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fetchEquipoData = useCallback(async () => {
    setEquipoLoading(true);
    try {
      const [configRes, usersRes] = await Promise.all([
        api.get("/onboarding/config"),
        api.get("/users/list")
      ]);
      setProfessionals(configRes.data?.professionals || []);
      setUsers(usersRes.data || []);
    } catch (e) {
      toastError(e);
    } finally {
      setEquipoLoading(false);
    }
  }, []);

  useEffect(() => {
    if (submenuIndex === 2) fetchEquipoData();
  }, [submenuIndex, fetchEquipoData]);

  const fetchSecuenciasData = useCallback(async () => {
    setSecuenciasLoading(true);
    try {
      const [configRes, promptsRes] = await Promise.all([
        api.get("/onboarding/config"),
        api.get("/prompt", { params: { pageNumber: 1 } })
      ]);
      setAppointmentPromptId(configRes.data?.business?.appointmentPromptId ?? null);
      setPrompts(promptsRes.data?.prompts || []);
    } catch (e) {
      toastError(e);
    } finally {
      setSecuenciasLoading(false);
    }
  }, []);

  useEffect(() => {
    if (submenuIndex === 4) fetchSecuenciasData();
  }, [submenuIndex, fetchSecuenciasData]);

  const handleLinkUser = async (professionalId, userId) => {
    try {
      await api.patch(`/onboarding/professionals/${professionalId}`, { userId: userId || null });
      toast.success(i18n.t("appointmentsModule.config.toastLinkOk"));
      const { data } = await api.get("/onboarding/config");
      setProfessionals(data?.professionals || []);
    } catch (e) {
      toastError(e);
    }
  };

  const goToOnboarding = (step) => {
    history.push(step != null ? `/onboarding?step=${step}` : "/onboarding");
  };

  const handleRecrearKanban = async () => {
    const pid = profileId ?? 1;
    if (pid < 1 || pid > 5) {
      toast.error(i18n.t("appointmentsModule.config.errorProfileFirst"));
      return;
    }
    setRecrearKanbanLoading(true);
    try {
      await api.post("/onboarding/activate", { profileId: pid });
      toast.success(i18n.t("appointmentsModule.config.toastKanbanOk"));
    } catch (e) {
      toastError(e);
    } finally {
      setRecrearKanbanLoading(false);
    }
  };

  const handleSecuenciasPromptChange = async (promptId) => {
    const value = promptId === "" || promptId == null ? null : Number(promptId);
    try {
      await api.patch("/onboarding/config", { appointmentPromptId: value });
      setAppointmentPromptId(value);
      toast.success(i18n.t("appointmentsModule.config.toastAgentOk"));
    } catch (e) {
      toastError(e);
    }
  };

  const currentCard = configCards[submenuIndex];
  const IconComponent = currentCard?.icon;

  return (
    <div className={classes.configLayout}>
      <aside className={classes.configSubmenu}>
        <List dense disablePadding>
          {configCards.map((card, idx) => (
            <ListItem
              key={card.id}
              button
              selected={submenuIndex === idx}
              onClick={() => setSubmenuIndex(idx)}
              className={classes.configSubmenuItem}
            >
              <ListItemText
                primary={card.title}
                primaryTypographyProps={{ variant: "body2", fontWeight: submenuIndex === idx ? 600 : 400 }}
              />
            </ListItem>
          ))}
        </List>
      </aside>

      <main className={classes.configContent}>
        {!loading && !profile && (
          <div className={classes.configFirstTimeBanner}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
              {i18n.t("appointmentsModule.config.firstTimeTitle")}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
              {i18n.t("appointmentsModule.config.firstTimeSubtitle")}
            </Typography>
            <Button variant="contained" color="primary" component={RouterLink} to="/onboarding" aria-label={i18n.t("appointmentsModule.config.firstTimeCta")}>
              {i18n.t("appointmentsModule.config.firstTimeCta")}
            </Button>
          </div>
        )}
        {profile && (
          <div className={classes.configProfileBanner}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {i18n.t("appointmentsModule.config.activeProfileTitle", { name: profile })}
            </Typography>
            <Typography variant="body2" color="textSecondary">{i18n.t("appointmentsModule.config.activeProfileSubtitle")}</Typography>
          </div>
        )}

        {currentCard && (
          <div className={classes.configCard}>
            <Typography className={classes.configCardTitle} component="div">
              {IconComponent && <IconComponent style={{ verticalAlign: "middle", marginRight: 8, fontSize: 20 }} />}
              {currentCard.title}
            </Typography>
            <Typography className={classes.configCardDesc}>{currentCard.desc}</Typography>

            {/* Equipo: tabla inline + botón a onboarding */}
            {currentCard.id === "equipo" && (
              <>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                  {i18n.t("appointmentsModule.config.equipoIntro")}
                </Typography>
                <div className={classes.configCardActions} style={{ marginBottom: 16 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => goToOnboarding(3)}
                    aria-label={i18n.t("appointmentsModule.config.equipoConfigureBtn")}
                  >
                    {i18n.t("appointmentsModule.config.equipoConfigureBtn")}
                  </Button>
                </div>
                {equipoLoading ? (
                  <Typography color="textSecondary">{i18n.t("appointmentsModule.config.loadingProfessionals")}</Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{i18n.t("appointmentsModule.config.tableProfessional")}</TableCell>
                        <TableCell>{i18n.t("appointmentsModule.config.tableRole")}</TableCell>
                        <TableCell>{i18n.t("appointmentsModule.config.tableLinkUser")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {professionals.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>{p.name}</TableCell>
                          <TableCell>{[p.roleLabel, p.specialty].filter(Boolean).join(" · ") || "—"}</TableCell>
                          <TableCell>
                            <FormControl size="small" style={{ minWidth: 220 }}>
                              <Select
                                value={p.whatsappUserId ?? ""}
                                onChange={(e) => handleLinkUser(p.id, e.target.value || null)}
                                displayEmpty
                              >
                                <MenuItem value="">{i18n.t("appointmentsModule.config.noneUser")}</MenuItem>
                                {users.map((u) => (
                                  <MenuItem key={u.id} value={u.id}>{u.name} ({u.email})</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      ))}
                      {professionals.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            {i18n.t("appointmentsModule.config.noProfessionals")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </>
            )}

            {/* Secuencias automáticas del Agente IA: dropdown Agente para citas + link a Prompts */}
            {currentCard.id === "secuencias" && (
              <>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                  {i18n.t("appointmentsModule.config.secuenciasIntro")}
                </Typography>
                {secuenciasLoading ? (
                  <Typography color="textSecondary">{i18n.t("appointmentsModule.config.loading")}</Typography>
                ) : (
                  <>
                    <FormControl size="small" style={{ minWidth: 280, marginBottom: 16, display: "block" }}>
                      <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 4 }}>
                        {i18n.t("appointmentsModule.config.agentLabel")}
                      </Typography>
                      <Select
                        value={appointmentPromptId ?? ""}
                        onChange={(e) => handleSecuenciasPromptChange(e.target.value || null)}
                        displayEmpty
                        fullWidth
                      >
                        <MenuItem value="">{i18n.t("appointmentsModule.config.noAgent")}</MenuItem>
                        {prompts.map((p) => (
                          <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                        {prompts.length === 0 && (
                          <MenuItem value="" disabled>{i18n.t("appointmentsModule.config.noAgentsList")}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      component={RouterLink}
                      to="/prompts"
                      aria-label={i18n.t("appointmentsModule.config.promptsLink")}
                    >
                      {i18n.t("appointmentsModule.config.promptsLink")}
                    </Button>
                  </>
                )}
              </>
            )}

            {/* Perfil: botón Recrear columnas Kanban (0258 D4) */}
            {currentCard.id === "perfil" && profileId != null && (
              <div className={classes.configCardActions} style={{ marginBottom: 16 }}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  {i18n.t("appointmentsModule.config.kanbanHint")}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={recrearKanbanLoading}
                  onClick={handleRecrearKanban}
                  aria-label={i18n.t("appointmentsModule.config.kanbanRecreate")}
                >
                  {recrearKanbanLoading ? i18n.t("appointmentsModule.config.kanbanProcessing") : i18n.t("appointmentsModule.config.kanbanRecreate")}
                </Button>
              </div>
            )}

            {/* Resto de secciones: botón Editar que lleva al onboarding al paso correcto */}
            {currentCard.id !== "equipo" && currentCard.id !== "secuencias" && (
              <div className={classes.configCardActions}>
                {currentCard.onboardingStep != null ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => goToOnboarding(currentCard.onboardingStep)}
                    aria-label={i18n.t("appointmentsModule.config.editSectionAria", { section: currentCard.title })}
                  >
                    {i18n.t("appointmentsModule.config.edit")}
                  </Button>
                ) : null}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
